import React from 'react';
import { Award, Star, TrendingUp, RefreshCcw } from 'lucide-react';
import '../EvaluationDesign.css';

/**
 * Team4Box Component - Refactored Design
 */
const Team4Box = ({ teamData, onUserSelect, compact = false }) => {
    
    // Process team data
    const processedData = (teamData || []).map(user => {
        const levels = user.niveaux || [];
        if (levels.length === 0) return null;

        const avgObtenu = infoAverage(levels, 'scoreObtenu', 'scoreManager', 'scoreCollab');
        const avgRequis = infoAverage(levels, 'scoreRequis');

        return {
            id: user.id,
            name: user.userFullName || user.nom || "Collaborateur",
            initiales: user.userFullName ? user.userFullName.trim().split(' ').map(n => n ? n[0] : "").join('').toUpperCase().substring(0, 2) : "??",
            x: avgObtenu,
            y: avgRequis,
            userObj: user
        };
    }).filter(Boolean);

    // Buckets based on threshold 2.5
    const experts = []; // High Maîtrise, Low Requis
    const stars = []; // High Maîtrise, High Requis
    const aDevelopper = []; // Low Maîtrise, High Requis
    const aRepositionner = []; // Low Maîtrise, Low Requis

    processedData.forEach(user => {
        if (user.x >= 2.5 && user.y >= 2.5) {
            stars.push(user);
        } else if (user.x >= 2.5 && user.y < 2.5) {
            experts.push(user);
        } else if (user.x < 2.5 && user.y >= 2.5) {
            aDevelopper.push(user);
        } else {
            aRepositionner.push(user);
        }
    });

    if (processedData.length === 0) {
        return (
            <div className="eval-empty">
                <h3>Matrice d'Équipe</h3>
                <p>Aucune donnée disponible pour projeter l'équipe.</p>
            </div>
        );
    }

    const BoxQuadrant = ({ title, description, icon: Icon, theme, users }) => (
        <div style={{ backgroundColor: theme.bg, borderRadius: '6px', padding: compact ? '10px' : '16px', display: 'flex', flexDirection: 'column', gap: compact ? '8px' : '12px', minHeight: compact ? '112px' : '160px', boxShadow: compact ? '0 1px 2px rgba(0,0,0,0.08)' : '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', color: theme.text }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: compact ? '12px' : '15px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h3>
                    <Icon size={compact ? 15 : 20} style={{ opacity: 0.9 }} />
                </div>
                <div style={{ fontSize: compact ? '9px' : '11px', marginTop: compact ? '4px' : '6px', opacity: 0.9, lineHeight: 1.35, textAlign: 'justify' }}>
                    {description}
                </div>
            </div>
            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', padding: compact ? '8px' : '12px', flex: 1, display: 'flex', flexWrap: 'wrap', gap: compact ? '5px' : '8px', alignContent: 'flex-start', minHeight: compact ? '42px' : '60px' }}>
                {users.length > 0 ? users.map(u => (
                    <div 
                        key={u.id}
                        onClick={() => onUserSelect && onUserSelect(u.userObj)}
                        title={`Maîtrise: ${u.x.toFixed(1)} | Requis: ${u.y.toFixed(1)}`}
                        style={{
                            backgroundColor: '#ffffff',
                            border: `1px solid ${theme.dot}50`,
                            color: '#1e293b', 
                            padding: compact ? '4px 8px' : '6px 12px',
                            borderRadius: '20px',
                            fontSize: compact ? '10px' : '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: compact ? '5px' : '8px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                        }}
                        onMouseEnter={(e) => { 
                            e.currentTarget.style.transform = 'translateY(-2px)'; 
                            e.currentTarget.style.backgroundColor = theme.dot; 
                            e.currentTarget.style.color = '#ffffff'; 
                            e.currentTarget.style.borderColor = theme.dot;
                            e.currentTarget.children[0].style.backgroundColor = '#ffffff';
                        }}
                        onMouseLeave={(e) => { 
                            e.currentTarget.style.transform = 'translateY(0)'; 
                            e.currentTarget.style.backgroundColor = '#ffffff'; 
                            e.currentTarget.style.color = '#1e293b'; 
                            e.currentTarget.style.borderColor = `${theme.dot}50`;
                            e.currentTarget.children[0].style.backgroundColor = theme.dot;
                        }}
                    >
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: theme.dot, transition: 'background-color 0.2s ease' }}></div>
                        {u.name}
                    </div>
                )) : null}
            </div>
        </div>
    );

    // Charte Graphique Officielle (Bleu #204385, Rouge/Corail #ee645f + neutres/variations)
    const themes = {
        experts: { bg: "#4672b8", text: "#ffffff", dot: "#4672b8" },        // Bleu clair (dérivé charte)
        star: { bg: "#204385", text: "#ffffff", dot: "#204385" },           // Bleu profond (charte principale)
        aDevelopper: { bg: "#ee645f", text: "#ffffff", dot: "#ee645f" },    // Rouge corail (charte principale)
        aRepositionner: { bg: "#6b7280", text: "#ffffff", dot: "#6b7280" }  // Gris neutre (charte)
    };

    return (
        <div style={{ padding: compact ? '8px' : '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: compact ? '10px' : '16px', marginBottom: compact ? '10px' : '24px' }}>
                <BoxQuadrant 
                    title="Experts" 
                    description="Compétences métier et soft skills surpassant largement les exigences de référence."
                    icon={Award} theme={themes.experts} users={experts} 
                />
                <BoxQuadrant 
                    title="Star" 
                    description="Parfaite maîtrise des compétences clés (métier et soft) sur un poste exigeant."
                    icon={Star} theme={themes.star} users={stars} 
                />
                <BoxQuadrant 
                    title="À développer" 
                    description="Écarts identifiés sur les compétences critiques du poste, nécessitant une mise à niveau."
                    icon={TrendingUp} theme={themes.aDevelopper} users={aDevelopper} 
                />
                <BoxQuadrant 
                    title="À repositionner" 
                    description="Décalage global des compétences vis-à-vis d'un profil cible faiblement exigeant."
                    icon={RefreshCcw} theme={themes.aRepositionner} users={aRepositionner} 
                />
            </div>

            <div style={{ marginTop: compact ? '8px' : '20px', paddingTop: compact ? '8px' : '20px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>Légende</h4>
                <div style={{ display: 'flex', gap: compact ? '12px' : '24px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: themes.experts.dot }}></div> Experts
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: themes.star.dot }}></div> Star
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: themes.aDevelopper.dot }}></div> À développer
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: themes.aRepositionner.dot }}></div> À repositionner
                    </div>
                </div>
                {/*<div style={{ marginTop: compact ? '8px' : '16px', fontSize: compact ? '10px' : '12px', color: '#94a3b8' }}>*/}
                {/*    Cliquez sur l'étiquette d'un collaborateur pour voir ses détails.*/}
                {/*</div>*/}
            </div>
        </div>
    );
};

const infoAverage = (levels, ...keys) => {
    if (!levels || levels.length === 0) return 0;
    const total = levels.reduce((sum, lvl) => {
        let val = 0;
        for (const key of keys) {
            if (lvl[key] !== undefined && lvl[key] !== null) {
                val = lvl[key];
                break;
            }
        }
        return sum + val;
    }, 0);
    return total / levels.length;
};

export default Team4Box;
